import { Feature } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { Circle, LineString, MultiLineString, Point } from 'ol/geom';
import { Feature as GeoJsonFeature, Point as GeoJsonPoint } from 'geojson';

import * as olProj from 'ol/proj';
import * as turfHelper from '@turf/helpers';
import nearestPointOnLine from '@turf/nearest-point-on-line';

/**
 * name/desc : are the descriptive texts for this routing project
 * time : is the timestamp in milliseconds, when this project was created or last edited
 */
export type TraceMetadataType = {
  name?: string;
  desc?: string;
  time: number;
};

export const TraceSequenceNumberTypeNames = ['alpha', 'number'] as const;
export type TraceSequenceNumberType = typeof TraceSequenceNumberTypeNames[number];
export type TraceTTSettingsType = {
  wayPointSequenceNumber: TraceSequenceNumberType
}

/**
 * xyz : are coordinates, normally in the current map projection
 * name/desc : are the descriptive texts for this point, normally only for waypoint's(poi), not routepoint's
 * symb : is the symbol name or url for a waypoint(pio), this is not used yet
 * time : is the timestamp in milliseconds, normally only for routepoint's
 * autoDraw : is only used for routepoint's and it indicates if the point was added by the auto routing tool
 * drawId : is the identifier when adding a previously stored (import_*_*) or a new feature (drawnPoint_*) on the map, this id is removed when storing the json (locally or remotely)
 * formattedXY: will be the formatted values of x & y to show in the html template (used for performance reasons), these values are removed when storing the json (locally or remotely)
 * formattedName: only used in the create component, to track changes in the waypoint name
 * formattedDesc: only used in the create component, to track changes in the waypoint description
 */
export type TraceWayPointDrawIdType = `${string & { __drawIdRegex: "(import_|drawnPoint_)\\d{1,}(_\\d{1,})?" }}`;
export type TraceWayPointType = {
  x: number;
  y: number;
  z?: number;
  name?: string;
  desc?: string;
  symb?: string;
  time?: number;
  autoDraw?: boolean;
  drawId?: TraceWayPointDrawIdType;
  formattedXY?: string;
  formattedName?: string;
  formattedDesc?: string;
};

/**
 * routePoints: every turning point on one route
 * wasTrack: we don't keep gpx tracks (trk) in a seperate array, we add them to the routes array (gpx rte) but we keep the metadata in boolean 'wasTrack'
 * name/desc : are the descriptive texts for this route, this is not used yet
 */
export type TraceRouteType = {
  routePoints: TraceWayPointType[];
  wasTrack: boolean;
  name?: string;
  desc?: string;
};

export class TraceRouteData {

  private static _updateCounter: number = 0;

  constructor(
    public epsgCode: string,
    public metadata: TraceMetadataType,
    public wayPoints: TraceWayPointType[],
    public routes: TraceRouteType[],
    public ttSettings?: TraceTTSettingsType,
  ) { }

  /**
   * methods for checking if object complies to TraceRouteData model
   */

  /**
   * main method for checking if object complies to TraceRouteData model
   * @param traceRouteData 
   * @returns 
   */
  static isTraceRouteData(
    traceRouteData: any
  ): traceRouteData is TraceRouteData {
    let isValid =
      TraceRouteData.getNativeClass(traceRouteData) === 'object' &&
      traceRouteData.hasOwnProperty('epsgCode') &&
      this.isEpsgCode(traceRouteData.epsgCode) &&
      traceRouteData.hasOwnProperty('metadata') &&
      TraceRouteData.getNativeClass(traceRouteData.metadata) === 'object' &&
      TraceRouteData.isTraceMetadataType(traceRouteData.metadata) &&
      traceRouteData.hasOwnProperty('wayPoints') &&
      TraceRouteData.getNativeClass(traceRouteData.wayPoints) === 'array' &&
      traceRouteData.hasOwnProperty('routes') &&
      TraceRouteData.getNativeClass(traceRouteData.routes) === 'array' &&
      (!traceRouteData.hasOwnProperty('ttSettings') || !traceRouteData.ttSettings || (TraceRouteData.getNativeClass(traceRouteData.ttSettings) === 'object' && TraceRouteData.isTraceTTSettingsType(traceRouteData.ttSettings)));
    if (isValid) {
      traceRouteData.routes.every((route: any) => {
        if (!TraceRouteData.isTraceRouteType(route)) {
          isValid = false;
          return false;
        }
        return true;
      });
    }
    if (isValid) {
      traceRouteData.wayPoints.every((wayPoint: any) => {
        if (!TraceRouteData.isTraceWayPointType(wayPoint)) {
          isValid = false;
          return false;
        }
        return true;
      });
    }

    return isValid;
  }

  static isTraceMetadataType(metadata: any): metadata is TraceMetadataType {
    return (
      TraceRouteData.getNativeClass(metadata) === 'object' &&
      (!metadata.hasOwnProperty('name') || typeof metadata.name === 'string') &&
      (!metadata.hasOwnProperty('desc') || typeof metadata.desc === 'string') &&
      metadata.hasOwnProperty('time') && typeof metadata.time === 'number'
    );
  }

  static isTraceTTSettingsType(ttSettings: any): ttSettings is TraceTTSettingsType {
    return (
      TraceRouteData.getNativeClass(ttSettings) === 'object' &&
      (typeof ttSettings.wayPointSequenceNumber === 'string' && TraceSequenceNumberTypeNames.includes(ttSettings.wayPointSequenceNumber))
    );
  }

  static isTraceWayPointType(wayPoint: any): wayPoint is TraceWayPointType {
    return (
      TraceRouteData.getNativeClass(wayPoint) === 'object' &&
      typeof wayPoint.x === 'number' &&
      typeof wayPoint.y === 'number' &&
      (!wayPoint.hasOwnProperty('z') || typeof wayPoint.z === 'number') &&
      (!wayPoint.hasOwnProperty('name') || typeof wayPoint.name === 'string') &&
      (!wayPoint.hasOwnProperty('desc') || typeof wayPoint.desc === 'string') &&
      (!wayPoint.hasOwnProperty('symb') || typeof wayPoint.symb === 'string') &&
      (!wayPoint.hasOwnProperty('time') || typeof wayPoint.time === 'number') &&
      (!wayPoint.hasOwnProperty('autoDraw') || typeof wayPoint.autoDraw === 'boolean') &&
      (!wayPoint.hasOwnProperty('drawId') || typeof wayPoint.drawId === 'string') &&
      (!wayPoint.hasOwnProperty('formattedXY') || typeof wayPoint.formattedXY === 'string')
    );
  }

  static isTraceRouteType(route: any): route is TraceRouteType {
    let isValid =
      TraceRouteData.getNativeClass(route) === 'object' &&
      typeof route.wasTrack === 'boolean' &&
      (!route.hasOwnProperty('name') || typeof route.name === 'string') &&
      (!route.hasOwnProperty('desc') || typeof route.desc === 'string') &&
      Array.isArray(route.routePoints);

    if (isValid) {
      route.routePoints.every((routePoint: any) => {
        if (!TraceRouteData.isTraceWayPointType(routePoint)) {
          isValid = false;
          return false;
        }
        return true;
      });
    }

    return isValid;
  }

  /* ******************************
   * conversion methods between OL geometries and TraceRouteData objects
   ****************************** */

  static convertTraceRouteToLineStringOrPointFeature(
    traceRoute: TraceRouteType
  ): Feature {
    let feature = null;

    if (traceRoute.routePoints.length > 0) {
      feature = new Feature();

      if (traceRoute.routePoints.length > 1) {
        const coordinates: Coordinate[] = [];
        const fixedPointIndexes: number[] = [];

        traceRoute.routePoints.forEach((routePoint: TraceWayPointType) => {
          coordinates.push([routePoint.x, routePoint.y]);

          if (typeof routePoint.autoDraw !== 'boolean' || !routePoint.autoDraw) {
            fixedPointIndexes.push(coordinates.length - 1);
          }
        });

        //during gpx file download openlayers will create a gpx file with tracks (trk) or routes (rte) tags, depending on the geometry type
        //track will be a multilinestring feature, route will be a linestring feature 
        feature.setGeometry(traceRoute.wasTrack ? new MultiLineString([new LineString(coordinates)]) : new LineString(coordinates));

        if (fixedPointIndexes.length > 0) {
          const gpx_extensions = {};
          if (traceRoute.wasTrack) {
            gpx_extensions['trkseg_fixedpoint_indexes'] = fixedPointIndexes;
          } else {
            gpx_extensions['rte_fixedpoint_indexes'] = fixedPointIndexes;
          }
          feature.set('gpx_extensions', gpx_extensions);
        }
      } else {
        feature.setGeometry(new Point([traceRoute.routePoints[0].x, traceRoute.routePoints[0].y]));
      }
      if (traceRoute.name) {
        feature.set('name', traceRoute.name);
      }
      if (traceRoute.desc) {
        feature.set('desc', traceRoute.desc);
      }
    }

    return feature;
  }

  static convertTraceWayPointToPointFeature(
    traceWayPoint: TraceWayPointType
  ): Feature {
    const feature = new Feature();
    feature.setGeometry(new Point([traceWayPoint.x, traceWayPoint.y]));

    if (traceWayPoint.symb) {
      feature.set('symb', traceWayPoint.symb);
    }

    if (traceWayPoint.name) {
      feature.set('name', traceWayPoint.name);
    }

    if (traceWayPoint.desc) {
      feature.set('desc', traceWayPoint.desc);
    }

    if (traceWayPoint.symb) {
      feature.set('symb', traceWayPoint.symb);
    }

    if (traceWayPoint.time) {
      feature.set('time', traceWayPoint.time);
    }

    return feature;
  }

  /*
  static convertMultiLineStringFeatureToTraceRoute(
    multiLineStringFeature: Feature
  ): TraceRouteType[] {
    const traceRoutes: TraceRouteType[] = [];

    let multiLineStringGeometry =
      multiLineStringFeature.getGeometry() as MultiLineString;

    const multiLineStringFeatureCopy = multiLineStringFeature.clone();

    multiLineStringGeometry
      .getLineStrings()
      .forEach((lineString: LineString) => {
        multiLineStringFeatureCopy.setGeometry(lineString);

        //multiLineString is normally a track
        traceRoutes.push(
          this.convertLineStringFeatureToTraceRoute(
            multiLineStringFeatureCopy,
            true
          )
        );

        //we don't want the same description for each line, only the first line?
        //multiLineStringFeatureCopy.set('name', null);
        //multiLineStringFeatureCopy.set('desc', null);
      });
    return traceRoutes;
  }

  static convertLineStringFeatureToTraceRoute(
    lineStringFeature: Feature,
    wasTrack: boolean
  ): TraceRouteType {
    const traceRoute: TraceRouteType = {
      routePoints: [],
      wasTrack: wasTrack,
    };

    if (lineStringFeature.get('name')) {
      traceRoute.name = lineStringFeature.get('name');
    }

    if (lineStringFeature.get('desc')) {
      traceRoute.desc = lineStringFeature.get('desc');
    }

    const lineStringGeometry = lineStringFeature.getGeometry() as LineString;

    lineStringGeometry.getCoordinates().forEach((coordinate: Coordinate) => {
      traceRoute.routePoints.push({
        x: coordinate[0],
         y: coordinate[1],
      });
    });

    return traceRoute;
  }
  */

  static convertPointFeatureToTraceWayPoint(
    pointFeature: Feature,
    currentPointProjection: string
  ): TraceWayPointType {
    const pointGeometry = pointFeature.getGeometry() as Point;

    const traceWayPoint: TraceWayPointType = {
      x: pointGeometry.getCoordinates()[0],
      y: pointGeometry.getCoordinates()[1],
      formattedXY: TraceRouteData.formatPointForHtmlView(pointGeometry, currentPointProjection)
    };

    if (pointFeature.get('name')) {
      traceWayPoint.name = pointFeature.get('name');
    }
    if (pointFeature.get('desc')) {
      traceWayPoint.desc = pointFeature.get('desc');
    }

    if (pointFeature.get('symb')) {
      traceWayPoint.symb = pointFeature.get('symb');
    }

    if (pointFeature.get('time')) {
      traceWayPoint.time = pointFeature.get('time');
    }

    if (pointFeature.get('autoDraw')) {
      traceWayPoint.autoDraw = pointFeature.get('autoDraw') ? true : false;
    }

    return traceWayPoint;
  }

  /* ******************************
   * conversion methods for trace objects to Coordinate[]
   ****************************** */

  /**
   * 
   * @param routes 
   * @param convertMultiLineToLineStringCoordinates if false and routes contains multiple lines, the returned array will be 2 dimensional, otherwise it will be a 1 dimensional array
   * @returns array of coordinates from all the routepoints in the routes array; if the end coordinate of a previous route matches the start coordinate of the next route, then this coordinate is not added
   */
  static convertTaceRoutesToCoordinates(routes: TraceRouteType[], convertMultiLineToLineStringCoordinates: boolean): Coordinate[] {
    let coordinates: Coordinate[] = [];

    if (Array.isArray(routes)) {
      routes.forEach((route) => {
        let routePointIndexPerRoute = -1;
        const lineStringCoords = [];
        route.routePoints.forEach((routePoint: TraceWayPointType) => {
          routePointIndexPerRoute++;
          if (routePointIndexPerRoute > 0 || coordinates.length === 0 || !TraceRouteData.matchingCoordinates(coordinates[coordinates.length - 1], [routePoint.x, routePoint.y])) {
            lineStringCoords.push([routePoint.x, routePoint.y]);
          }
        });
        if (convertMultiLineToLineStringCoordinates) {
          coordinates = [].concat(coordinates, lineStringCoords);
        } else {
          coordinates.push(lineStringCoords)
        }
      });
    }

    return coordinates
  }

  static convertTaceRoutePointsToCoordinates(routePoints: TraceWayPointType[]): Coordinate[] {
    let coordinates: Coordinate[] = [];

    if (Array.isArray(routePoints)) {
      routePoints.forEach((routePoint) => {
        coordinates.push([routePoint.x, routePoint.y]);
      });
    }

    return coordinates
  }

  /* ******************************
   * other utility methods
   ****************************** */

  static getEmptyTraceRouteDataObject(currentMapProjection: string) {
    return new TraceRouteData(currentMapProjection ? currentMapProjection : 'EPSG:4326', { time: -1 }, [], [], null);
  }

  /**
   * filter out null/undefined values from arrays and objects
   * 
   * @param obj 
   * @param isForStorage if true, empty strings & key drawId will be removed too
   * @returns 
   */
  static getFilteredTraceRouteDataArray(obj: any[], isForStorage: boolean) {
    if (TraceRouteData.getNativeClass(obj) === 'array') {
      //remove the null's and undefined's
      let filteredArray = obj.filter((e: any) => e);
      //forEach doesn't work for updating items
      //normally we only have objects in the arrays of our model
      for (let i = 0; i < filteredArray.length; i++) {
        if (TraceRouteData.getNativeClass(filteredArray[i]) === 'object') {
          filteredArray[i] = TraceRouteData.getFilteredTraceRouteDataObject(
            filteredArray[i],
            isForStorage
          );
        }
      }
      return filteredArray;
    } else {
      /*
      throw new Error(
        'object was not an array, unable to filter out null/undefined'
      );
      */
      return [];
    }
  }

  /**
   * 
   * @param obj 
   * @param isForStorage if true, empty strings & key drawId will be removed too
   * @returns 
   */
  static getFilteredTraceRouteDataObject(obj: any, isForStorage: boolean) {
    if (TraceRouteData.getNativeClass(obj) === 'object') {
      const filteredObj = {};
      for (let key in obj) {
        const keyValueType = TraceRouteData.getNativeClass(obj[key]);
        switch (keyValueType) {
          case 'object': {
            filteredObj[key] = this.getFilteredTraceRouteDataObject(
              obj[key],
              isForStorage
            );
            break;
          }
          case 'array': {
            filteredObj[key] = this.getFilteredTraceRouteDataArray(
              obj[key],
              isForStorage
            );
            break;
          }
          case 'string': {
            if (isForStorage) {
              if (key !== 'drawId' 
                && key !== 'formattedXY' 
                && key !== 'formattedName' 
                && key !== 'formattedDesc' 
                && obj[key].trim().length > 0) {
                filteredObj[key] = obj[key];
              }
            } else {
              switch (key) {
                case 'name': {
                  filteredObj[key] = TraceRouteData.getFilteredHtmlString(obj[key], true, false);
                  break;
                }
                case 'desc': {
                  filteredObj[key] = TraceRouteData.getFilteredHtmlString(obj[key], false, false);
                  break;
                }
                default: {
                  filteredObj[key] = obj[key];
                }
              }
            }

            break;
          }
          case 'boolean': {
            if (!isForStorage || key !== 'autoDraw' || obj['autoDraw']) {
              filteredObj[key] = obj[key];
            }
            break;
          }
          case 'number': {
            //x y max 6 decimal number, we also show 6 numbers in method formatPointForHtmlView
            if (isForStorage && /^[x-y]$/.test(key)) {
              const rounded = TraceRouteData.roundXYZ(obj[key], 6);
              filteredObj[key] = rounded > -1 ? rounded : parseFloat(obj[key].toFixed(6));
            } else {
              filteredObj[key] = obj[key];
            }
            break;
          }
          case 'null':
          case 'undefined': {
            //don't add
            break;
          }
          default: {
            filteredObj[key] = obj[key];
          }
        }
      }
      return filteredObj;
    } else {
      /*
      throw new Error(
        'object was not a simple object, unable to filter out null/undefined'
      );
      */
      return obj;
    }
  }

  static roundXYZ = function (value: number, precision: number): number {
    if (typeof value !== 'number' || typeof precision !== 'number') return -1;
    let int = Math.floor(value).toString();
    if (int[0] == '-' || int[0] == '+') int = int.slice(1, int.length)
    return parseFloat(value.toPrecision(int.length + precision));
  }

  /**
   * 
   * @param htmlString 
   * @param isName true if the text is meant as name for a waypoint, false if it's the description of a waypoint
   * @param isTextareaInput true if the text comes from local editing from a textarea (multiline, newlines chars), false if the data comes from import/permalink/local storage or a normal input field (not multiline)
   * @returns the checked string; returns a  null if it's not a string or an empty string
   */
  static getFilteredHtmlString(htmlString: string, isName: boolean, isTextareaInput: boolean): string {
    htmlString = typeof htmlString === "string" ? htmlString.trim() : '';
    if (htmlString.length > 0) {
      try {
        //string coming from xml file?
        htmlString = htmlString.replace(/(&lt;)/g, '<').replace(/(&gt;)/g, '>');

        if (isName) {
          //replace newline by space, double spaces are removed below
          htmlString = htmlString.replace(/(\r\n)/gm, ' ');
          htmlString = htmlString.replace(/(\r|\n)/gm, ' ');
          //replace br by space, double spaces are removed below
          htmlString = htmlString.replace(/(<br\s*[\/]?>)/gi, ' ');
          //we could replace < and > (we don't want tags in the title) but angular updates the & to &amp; (?)
          //htmlString = htmlString.replace(/</g, '&lt;').replace(/>/g, '&gt;');
          //alternative
          const tempHtmlElement = document.createElement("div");
          tempHtmlElement.innerHTML = htmlString;
          htmlString = tempHtmlElement.textContent;
          //we could replace " and ' but angular updates the & to &amp; (?)
          //htmlString = htmlString.replace(/"/g, '&quot;').replace(/'/g, '&apos;');
          //and finally remove double spaces 
          htmlString = htmlString.replace(/\s{2,}/g, ' ');
        } else {
          //remove img tags 
          let temp = '';
          let position = -1;
          while ((position = htmlString.toLowerCase().indexOf("<img ")) > -1) {
            temp += htmlString.substring(0, position);
            htmlString = htmlString.substring(position + 5);
            htmlString = htmlString.substring(htmlString.indexOf(">") + 1) + ' [image] ';
          }
          htmlString = temp + htmlString;

          const breakRegex = new RegExp('<br\s*[\/]?>', 'gmi');
          //if no br tags in the text, newlines will become br's
          //unless we save a locally edited text from a textarea
          if (isTextareaInput || !breakRegex.test(htmlString)) {
            htmlString = htmlString.replace(/(\r\n)/gm, '<br />');
            htmlString = htmlString.replace(/(\r|\n)/gm, '<br />');
          }
          //replace newline by space, double spaces are removed below
          htmlString = htmlString.replace(/(\r\n)/gm, ' ');
          htmlString = htmlString.replace(/(\r|\n)/gm, ' ');
          //wysiwyg editors often create empty p tags
          htmlString = htmlString.replace(/(<p\s?>\s?<\/p\s?>)/gi, '');
          //replace p tags with a simple br
          htmlString = htmlString.replace(/(<p\s?>)/gi, '');
          htmlString = htmlString.replace(/(<\/p\s?>)/gi, '<br />');
          //double br's will become one 
          htmlString = htmlString.replace(/(<br\s*[\/]?>){2,}/gi, '<br />');
          //OR allow empty line ? more then 2 br's will become 2  >> htmlString = htmlString.replace(/(<br\s*[\/]?>){3,}/gi, '<br /><br />');
          //remove a br tag if it's at the end of the string (it will )
          htmlString = htmlString.replace(/(<br\s*[\/]?>)$/gi, '');
          //remove double spaces
          htmlString = htmlString.replace(/\s{2,}/g, ' ');
          //we leave tags?
          //href add target="_blank"
          //remove style & class attributes

          temp = '';
          while ((position = htmlString.toLowerCase().indexOf("href=")) > -1) {
            const quoteSign = htmlString.substring(position + 5, position + 6);
            let urlAsTitle = htmlString.substring(position + 6);
            urlAsTitle = urlAsTitle.substring(0, urlAsTitle.indexOf(quoteSign)).replace(/"/g, '');
            if (temp === '' && htmlString.indexOf('title="' + urlAsTitle + '"') > -1) {
              //this html string is already checked before by this method
              break;
            }
            temp += htmlString.substring(0, position) + ' target="_blank" title="' + urlAsTitle + '" href=';
            htmlString = htmlString.substring(position + 5);
          }
          htmlString = temp + htmlString;

          temp = '';
          while ((position = htmlString.toLowerCase().indexOf("style=")) > -1) {
            const quoteSign = htmlString.substring(position + 6, position + 7);
            temp += htmlString.substring(0, position);
            htmlString = htmlString.substring(position + 7);
            //while (htmlString.indexOf("\\" + quoteSign) < htmlString.indexOf(quoteSign)) {temp += htmlString.substring(0, htmlString.indexOf("\\" + quoteSign) + 2);htmlString = htmlString.substring(htmlString.indexOf("\\" + quoteSign) + 2) }
            htmlString = htmlString.substring(htmlString.indexOf(quoteSign) + 1);
          }
          htmlString = temp + htmlString;

          temp = '';
          while ((position = htmlString.toLowerCase().indexOf("class=")) > -1) {
            const quoteSign = htmlString.substring(position + 6, position + 7);
            temp += htmlString.substring(0, position);
            htmlString = htmlString.substring(position + 7);
            htmlString = htmlString.substring(htmlString.indexOf(quoteSign) + 1);
          }
          htmlString = temp + htmlString;

          //remove javascript (href/onclick)
          //angular does this automatically
          /*
          htmlString = htmlString.replace(/href="\s*javascript\s*:\s*[^"]+"/gi, 'href="#"');
          htmlString = htmlString.replace(/onclick="[^"]+"/ig, '');
          */
        }
      } catch (error) {
        htmlString = '';
        console.log("ERROR: unable to check html string : " + error);
      }
    }

    return htmlString.length > 0 ? htmlString : null;
  }

  /**
   * 
   * @param htmlString 
   * @param isForTextArea
   * @returns string where br tags are replaced with newline characters (all other tags remain); returns an empty string if htmlString is null
   */
  /*
  static convertHtmlToInputValue(htmlString: string, isForTextArea: boolean): string {
    let convertedHtml = "";
    if (typeof htmlString === "string") {
      //normally we don't need the multiline flag gmi
      if (isForTextArea) {
        convertedHtml = htmlString.replace(/(<br\s*[\/]?>)/gi, '\r\n');
      } else {
        convertedHtml = htmlString.replace(/(<br\s*[\/]?>)/gi, '');
      }
      convertedHtml = convertedHtml.replace(/(&lt;)/gi, '<')
        .replace(/(&gt;)/gi, '>')
        .replace(/(&quot;)/gi, '"')
        .replace(/(&apos;)/gi, "'")
    }
    return convertedHtml;
  }
    */

  /**
   * 
   * method for ordering wayPoints along a route
   * 
   * @param routes 
   * @param dataProjection 
   * @param currentMapProjection 
   * @param returnInCurrentMapProjection 
   * @returns 
   */
  static orderWayPointsAlongRoute(
    wayPoints: TraceWayPointType[],
    routes: TraceRouteType[],
    dataProjection: string,
    currentMapProjection: string,
    returnInCurrentMapProjection: boolean
  ): TraceWayPointType[] {
    //we don't want to update the referenced array
    wayPoints = TraceRouteData.deepCopy(wayPoints);
    const orderedWayPoints: TraceWayPointType[] = [];

    currentMapProjection = this.isEpsgCode(currentMapProjection) ? currentMapProjection.toUpperCase() : null;
    dataProjection = this.isEpsgCode(dataProjection) ? dataProjection.toUpperCase() : null;
    let currentDataProjection = dataProjection;

    if (currentMapProjection && dataProjection && routes && wayPoints && routes.length > 0 && routes[0].routePoints.length > 1 && wayPoints.length > 0) {
      //we don't want to update the referenced array
      routes = TraceRouteData.deepCopy(routes);

      //turf needs 'EPSG:4326'
      if (dataProjection !== 'EPSG:4326') {
        routes.forEach((route) => {
          route.routePoints.forEach((routePoint) => {
            const coordinate = this.updatePointProjection(
              [routePoint.x, routePoint.y],
              dataProjection,
              'EPSG:4326'
            );
            routePoint.x = coordinate[0];
            routePoint.y = coordinate[1];
          });
        });
        wayPoints.forEach((wayPoint) => {
          const coordinate = this.updatePointProjection(
            [wayPoint.x, wayPoint.y],
            dataProjection,
            'EPSG:4326'
          );
          wayPoint.x = coordinate[0];
          wayPoint.y = coordinate[1];
        });

        currentDataProjection = 'EPSG:4326';
      }

      const multiLineStringCoords = [];
      routes.forEach((route) => {
        const lineStringCoords = [];
        route.routePoints.forEach((wayPoint) => {
          lineStringCoords.push([wayPoint.x, wayPoint.y]);
        });
        multiLineStringCoords.push(lineStringCoords);
      });

      const turfLine = turfHelper.multiLineString(multiLineStringCoords);
      const turfPoints = new Array(wayPoints.length);
      let count = 0;
      wayPoints.forEach((wayPoint) => {
        turfPoints[count] = [];
        turfPoints[count].push(count);
        turfPoints[count].push(
          nearestPointOnLine(turfLine, [wayPoint.x, wayPoint.y], {
            units: 'meters',
          })
        );
        count++;
      });
      turfPoints.sort(function (a, b) {
        return a[1].properties.location - b[1].properties.location;
      });

      count = 0;

      turfPoints.forEach((result) => {
        orderedWayPoints.push(wayPoints[result[0]]);
        count++;
      });
    } else {
      if (wayPoints && wayPoints.length > 0) {
        orderedWayPoints.push(...wayPoints);
      }
    }

    if (
      currentMapProjection && dataProjection &&
      ((!returnInCurrentMapProjection && currentDataProjection !== dataProjection) ||
        (returnInCurrentMapProjection && currentDataProjection !== currentMapProjection))
    ) {
      //routes are not returned here, so no need to update projection for routes again
      orderedWayPoints.forEach((orderedWayPoint) => {
        const coordinate = this.updatePointProjection(
          [orderedWayPoint.x, orderedWayPoint.y],
          currentDataProjection,
          returnInCurrentMapProjection ? currentMapProjection : dataProjection
        );
        orderedWayPoint.x = coordinate[0];
        orderedWayPoint.y = coordinate[1];
      });
    }

    return orderedWayPoints;
  }

  /**
   * method for getting closest routePoint for a point on the map
   * 
   * @param point 
   * @param routes 
   * @param currentMapProjection 
   * @returns 
   */
  static getClosestRoutePointForMapPoint(
    point: Point,
    routes: TraceRouteType[],
    currentMapProjection: string
  ): Point {
    let resultPoint: Point = null;
    currentMapProjection = this.isEpsgCode(currentMapProjection) ? currentMapProjection.toUpperCase() : null;

    if (currentMapProjection && point && routes && routes.length > 0) {

      const wgs84Point = currentMapProjection !== 'EPSG:4326' ? point.clone().transform(currentMapProjection, 'EPSG:4326') : point.clone();

      routes.forEach((route: TraceRouteType) => {

        if (route.routePoints.length > 1) {

          let routeAsLineStringCoordinates = this.convertTaceRoutePointsToCoordinates(route.routePoints);
          if (currentMapProjection !== 'EPSG:4326') {
            routeAsLineStringCoordinates = new LineString(routeAsLineStringCoordinates).transform(currentMapProjection, 'EPSG:4326').getCoordinates();
          }

          const routeAsLineString = turfHelper.lineString(routeAsLineStringCoordinates);

          const nearestPointOnRoute: GeoJsonFeature<GeoJsonPoint> =
            nearestPointOnLine(routeAsLineString, wgs84Point.getCoordinates(), {
              units: 'meters',
            });

          if (nearestPointOnRoute) {
            let newResultPoint: Point;

            if (currentMapProjection !== 'EPSG:4326') {
              newResultPoint = new Point(this.updatePointProjection(
                nearestPointOnRoute.geometry.coordinates,
                'EPSG:4326',
                currentMapProjection
              ));
            } else {
              newResultPoint = new Point(nearestPointOnRoute.geometry.coordinates);
            }

            if (!resultPoint || new LineString([point.getCoordinates(), resultPoint.getCoordinates()]).getLength() > new LineString([point.getCoordinates(), newResultPoint.getCoordinates()]).getLength()) {
              resultPoint = newResultPoint;
            }
          }
        }
      });
    }

    return resultPoint;
  }

  /**
   * 
   * @param pointOnRoute 
   * @param routes 
   * @param currentMapProjection current map porjection e.g. 'EPSG:4326'
   * @param returnFirstResultOnly if true and the route overlaps itself, this function will return more then one linestring (ordered by length from the starting point) 
   * @param searchRadius pointOnRoute should lay in this radius around the route; optional, value in meters, value higher than zero, defaults to 5 meters
   * @returns line from startpoint until pointOnRoute
   */
  static getRouteUntilPoint(
    pointOnRoute: Point,
    routes: TraceRouteType[],
    currentMapProjection: string,
    returnFirstResultOnly: boolean,
    searchRadius?: number
  ): LineString[] {

    let resultLineStrings: LineString[] = [];
    currentMapProjection = this.isEpsgCode(currentMapProjection) ? currentMapProjection.toUpperCase() : null;
    searchRadius = typeof searchRadius === 'number' && searchRadius > 0 ? searchRadius : 5

    if (currentMapProjection && pointOnRoute && routes && routes.length > 0) {
      //turfjs line-intersect only returns the first intersect with a line (e.g. when a line makes a 180° turn, it should return 2 intersections)
      //own solution, inspect every line segment      
      const routeAsLineStringCoordinates = [];
      let previousRoutePoint: TraceWayPointType;
      const extentForIntersectionCheck = new Circle(pointOnRoute.getCoordinates(), typeof searchRadius === 'number' && searchRadius > 0 ? searchRadius : 5).getExtent();
      const coordOnRoute_wgs84: Coordinate = currentMapProjection !== 'EPSG:4326' ? pointOnRoute.clone().transform(currentMapProjection, 'EPSG:4326').getCoordinates() : pointOnRoute.getCoordinates();

      routes.every((route) => {
        route.routePoints.every((wayPoint) => {

          if (previousRoutePoint) {
            let lineSegment = new LineString([[previousRoutePoint.x, previousRoutePoint.y], [wayPoint.x, wayPoint.y]]);

            if (lineSegment.intersectsExtent(extentForIntersectionCheck)) {

              if (currentMapProjection !== 'EPSG:4326') {
                lineSegment.transform(currentMapProjection, 'EPSG:4326');
              }

              const turfLine = turfHelper.lineString(lineSegment.getCoordinates());
              const nearestPointOnRoute: GeoJsonFeature<GeoJsonPoint> = nearestPointOnLine(turfLine, coordOnRoute_wgs84, { units: 'meters', });

              let resultCoordinate: Coordinate;

              if (nearestPointOnRoute) {
                if (currentMapProjection !== 'EPSG:4326') {
                  resultCoordinate = this.updatePointProjection(
                    nearestPointOnRoute.geometry.coordinates,
                    'EPSG:4326',
                    currentMapProjection
                  );
                } else {
                  resultCoordinate = nearestPointOnRoute.geometry.coordinates
                }
              } else {
                //on error, original point
                resultCoordinate = pointOnRoute.getCoordinates()
              }

              const copiedCoordinates = routeAsLineStringCoordinates.slice(0);
              copiedCoordinates.push(resultCoordinate);
              const resultLine = new LineString(copiedCoordinates);

              //the circle will most of the time intersect the line two times, we try to only take one intersection ((searchRadius * 2) + extramargin))
              if (resultLineStrings.length === 0 || (resultLineStrings[resultLineStrings.length - 1].getLength() + ((searchRadius * 2) + 10)) < resultLine.getLength()) {
                resultLineStrings.push(resultLine);
                //console.log("added")
              }
              //else { console.log("ignored") }
            }
          }

          previousRoutePoint = wayPoint;
          routeAsLineStringCoordinates.push([wayPoint.x, wayPoint.y]);

          return returnFirstResultOnly && resultLineStrings.length > 0 ? false : true;
        });

        return returnFirstResultOnly && resultLineStrings.length > 0 ? false : true;
      });

    }

    return resultLineStrings;

  }

  /**
   * 
   * @param traceRouteData 
   * @param mapProjectionEpsgCode 
   * @returns copy of object with transformed waypoints/routePoints; on error it returns null
   */
  static convertTraceRouteDataToMapProjection(
    traceRouteData: TraceRouteData,
    mapProjectionEpsgCode: string,
  ): TraceRouteData {

    if (traceRouteData && this.isEpsgCode(traceRouteData.epsgCode) && this.isEpsgCode(mapProjectionEpsgCode)) {
      try {

        //compare epsg codes
        if (traceRouteData.epsgCode.toUpperCase() !== mapProjectionEpsgCode.toUpperCase()) {

          //we take a copy?
          traceRouteData = TraceRouteData.deepCopy(traceRouteData);

          if (traceRouteData.wayPoints) {
            traceRouteData.wayPoints.forEach((wayPoint) => {
              const coordinates = this.updatePointProjection([wayPoint.x, wayPoint.y], traceRouteData.epsgCode, mapProjectionEpsgCode);
              wayPoint.x = coordinates[0];
              wayPoint.y = coordinates[1];
            });
          }

          if (traceRouteData.routes) {
            traceRouteData.routes.forEach((route) => {
              if (route && route.routePoints) {
                route.routePoints.forEach((routePoint) => {
                  const coordinates = this.updatePointProjection([routePoint.x, routePoint.y], traceRouteData.epsgCode, mapProjectionEpsgCode);
                  routePoint.x = coordinates[0];
                  routePoint.y = coordinates[1];
                });
              }
            });
          }

        }

      } catch (error) {
        console.log('unable to transform traceRouteData to map projection : ' + error);
        traceRouteData = null;
      }
    } else {
      traceRouteData = null;
    }

    return traceRouteData;
  }


  /**
 * the three paramaters should already be checked and valid
 * 
 * @param metadata
 * @param wayPoints 
 * @param routes 
 * @returns 
 */
  static getTraceRouteDataImportSize(metadata: TraceMetadataType, wayPoints: TraceWayPointType[], routes: TraceRouteType[], ttSettings: TraceTTSettingsType): number {
    //the epsg code is not really important
    return TraceRouteData.getTraceRouteDataSize(new TraceRouteData(
      'EPSG:4326',
      metadata,
      wayPoints,
      routes,
      ttSettings));
  }

  static getTraceRouteDataSize(traceRouteData: TraceRouteData): number {
    if (traceRouteData) {
      try {
        //return new TextEncoder().encode(JSON.stringify(traceRouteData)).length
        //return JSON.stringify(traceRouteData).length
        return new Blob([JSON.stringify(traceRouteData)]).size;
      } catch (error) { }
    }
    return -1;
  }

  static getNativeClass(obj: any): string {
    if (typeof obj === 'undefined') {
      return 'undefined';
    } else if (obj === null) {
      return 'null';
    } else
      return Object.prototype.toString
        .call(obj)
        .match(/^\[object\s(.*)\]$/)[1]
        .toLowerCase();
  }

  static isEmptyObject(obj: {}): boolean {
    for (let prop in obj) {
      if (obj.hasOwnProperty(prop))
        return false;
    }
    return true;
  }

  static updatePointProjection(
    point: Coordinate,
    fromProjectionEpsg: string,
    toProjectionEpsg: string
  ) {
    fromProjectionEpsg = this.isEpsgCode(fromProjectionEpsg) ? fromProjectionEpsg.toUpperCase() : '';
    toProjectionEpsg = this.isEpsgCode(toProjectionEpsg) ? toProjectionEpsg.toUpperCase() : '';

    if (fromProjectionEpsg === toProjectionEpsg) {
      return point;
    } else {
      return olProj.transform(point, fromProjectionEpsg, toProjectionEpsg);
    }
  }

  static updateFormattedXYStrings(traceRouteData: TraceRouteData, currentPointProjection: string) {
    if (traceRouteData) {
      if (traceRouteData.wayPoints) {
        traceRouteData.wayPoints.forEach((wayPoint) => {
          wayPoint.formattedXY = TraceRouteData.formatPointForHtmlView(new Point([wayPoint.x, wayPoint.y]), currentPointProjection);
        });
      }
      if (traceRouteData.routes) {
        traceRouteData.routes.forEach((route) => {
          route.routePoints.forEach((routePoint) => {
            routePoint.formattedXY = TraceRouteData.formatPointForHtmlView(new Point([routePoint.x, routePoint.y]), currentPointProjection);
          });
        });
      }
    }
  }

  static addTempDrawIdsToPoints(tracePoints: TraceWayPointType[]) {
    if (Array.isArray(tracePoints)) {
      this._updateCounter++;
      let count = 0;
      tracePoints.forEach((tracePoint) => {
        if (!tracePoint.drawId) {
          const tmp = ('import_' + this._updateCounter + '_' + count++) as TraceWayPointDrawIdType;
          tracePoint.drawId = tmp;
        }
      });
    }
  }

  static formatPointForHtmlView(point: Point, currentPointProjection: string) {
    if (point) {
      //method getFilteredTraceRouteDataObject transforms xy to 6 positions, update if necessary
      let coordinate: Coordinate = TraceRouteData.updatePointProjection(point.getCoordinates(), currentPointProjection, 'EPSG:4326');
      return (
        '<span class="latlon"> latlon  </span>' +
        coordinate[1].toFixed(6) +
        ', ' +
        coordinate[0].toFixed(6)
      );
    } else {
      return '<span class="latlon">latlon  </span> -, -';
    }
  }

  static formatRouteLength(lengthOfRoute: number, forHtml: boolean, returnStringOnZero?: string) {
    lengthOfRoute = typeof lengthOfRoute !== 'number' || lengthOfRoute < 0 ? 0 : lengthOfRoute;
    if (lengthOfRoute < 1000) {
      lengthOfRoute = Math.floor(lengthOfRoute);
      return returnStringOnZero && lengthOfRoute === 0 ? returnStringOnZero : lengthOfRoute + (forHtml ? '<span>m</span>' : 'm');
    } else {
      const formattedValue = ((Math.round(lengthOfRoute / 10) * 10) / 1000).toFixed(2).replace(/(0)+$/, '').replace(/\.$/, '');
      return formattedValue + (forHtml ? '<span>km<span>' : 'km');
    }
  }

  static formatRouteTime(time: number, returnStringOnZero?: string) {
    if (time < 0) {
      return returnStringOnZero ? returnStringOnZero : '-';
    }

    const hours = Math.floor(time / 60);
    const minutes = (time % 60);
    return /*(hours < 10 ? '0' : '') +*/ hours + ':' + (minutes < 10 ? '0' : '') + minutes;
  }

  static formatTime(time: number, addSeconds: boolean, forHtml: boolean, returnStringOnError?: string) {
    if (time <= new Date('1970-01-01T00:00:00').getTime()) {
      return returnStringOnError ? returnStringOnError : '--/--/---';
    }
    const metadataDate = new Date(time);
    return metadataDate.getFullYear() + '-' + ((metadataDate.getMonth() + 1) < 10 ? '0' : '') + (metadataDate.getMonth() + 1) + '-' + (metadataDate.getDate() < 10 ? '0' : '') + metadataDate.getDate() + (forHtml ? ' &nbsp;' : '  ') + (metadataDate.getHours() < 10 ? '0' : '') + metadataDate.getHours() + ':' + (metadataDate.getMinutes() < 10 ? '0' : '') + metadataDate.getMinutes() + (addSeconds ? ':' + (metadataDate.getSeconds() < 10 ? '0' : '') + metadataDate.getSeconds() : '');
  }

  static matchingCoordinates(coordinate1: Coordinate, coordinate2: Coordinate) {
    if (TraceRouteData.isCoordinate(coordinate1) && TraceRouteData.isCoordinate(coordinate2)) {
      for (let i = 0; i < coordinate1.length; i++) {
        if (coordinate1[i] !== coordinate2[i]) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  static coordinatesCloserThanDistance(coordinate1: Coordinate, coordinate2: Coordinate, distanceInMeters: number) {
    if (TraceRouteData.isCoordinate(coordinate1) && TraceRouteData.isCoordinate(coordinate2)) {
      return new LineString([coordinate1, coordinate2]).getLength() <= distanceInMeters;
    }
    return false;
  }

  static coordinatesCloserThanPixels(coordinate1: Coordinate, coordinate2: Coordinate, distanceInPixels: number, pointResolution: number) {
    if (TraceRouteData.isCoordinate(coordinate1) && TraceRouteData.isCoordinate(coordinate2) && distanceInPixels > 0 && pointResolution > 0) {
      return (new LineString([coordinate1, coordinate2]).getLength() / pointResolution) <= distanceInPixels;
    }
    return false;
  }

  static isCoordinate(coordinate: any): coordinate is Coordinate {
    return Array.isArray(coordinate) && coordinate.length === 2 && typeof coordinate[0] === 'number' && typeof coordinate[1] === 'number';
  }

  static deepCopy(json: any) {
    try {
      //the easiest way
      return JSON.parse(JSON.stringify(json));
    } catch (error) {
      console.log("unable to create deep copy of json :");
      console.log(json);
    }
    return {};
  }

  static isEpsgCode(epsgCode: string) {
    return typeof epsgCode === 'string' && /^epsg:[0-9]{4,5}$/i.test(epsgCode);
  }

  static getTraceTTSetting(settings: TraceTTSettingsType, settingsName: string) {
    if (TraceRouteData.getNativeClass(settings) === 'object' && typeof settingsName === 'string' && settings.hasOwnProperty(settingsName) && settings[settingsName]) {
      return settings[settingsName];
    }
  }

  static convertWayPointIndexToLetter(numb: number, uppercase = true) {
    const base = uppercase ? 64 : 96;
    return String.fromCharCode(base + numb);
  }
}
